import { IMyWindow } from '@aca-new/app/shared/interfaces/my-window.interface';
import { WINDOW } from '@aca-new/app/shared/tokens/window.token';
import { Inject, Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, fromEvent, Subscription } from 'rxjs';
import { debounceTime, share } from 'rxjs/operators';

/**
 * Service for monitoring window resize events.
 * Provides BehaviorSubject observables that emit window dimensions on resize.
 *
 * This service follows the singleton pattern (providedIn: 'root') to ensure
 * only one instance exists throughout the application, preventing multiple
 * event listeners and improving performance.
 *
 * The service automatically initializes when first injected, so no manual
 * initialization is required. Simply inject it in any component or service.
 * @example
 * ```typescript
 * constructor(private readonly _windowResizeService: WindowResizeService) {}
 *
 * ngOnInit(): void {
 *   // Subscribe to resize events
 *   this._windowResizeService.resizeBehaviourSubject$
 *     .pipe(untilDestroyed(this))
 *     .subscribe((event: Event) => {
 *       const width = (event.target as Window).innerWidth;
 *       const height = (event.target as Window).innerHeight;
 *       // Handle resize
 *     });
 *
 *   // Or use width/height observables directly
 *   this._windowResizeService.widthBehaviourSubject$
 *     .pipe(untilDestroyed(this))
 *     .subscribe((width: number) => {
 *       console.log('Window width:', width);
 *     });
 * }
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class WindowResizeService implements OnDestroy {
  /**
   * BehaviorSubject that emits window resize events.
   * Debounced by 100ms to optimize performance.
   * Shares the subscription to prevent multiple listeners.
   */
  public readonly resizeBehaviourSubject$: BehaviorSubject<Event>;

  /**
   * BehaviorSubject that emits window width on resize.
   * Debounced by 100ms to optimize performance.
   * Emits current width immediately on subscription.
   */
  public readonly widthBehaviourSubject$: BehaviorSubject<number>;

  /**
   * BehaviorSubject that emits window height on resize.
   * Debounced by 100ms to optimize performance.
   * Emits current height immediately on subscription.
   */
  public readonly heightBehaviourSubject$: BehaviorSubject<number>;

  private readonly _resizeSubscription: Subscription;

  public constructor(@Inject(WINDOW) private readonly _window: IMyWindow) {
    // Initialize BehaviorSubjects with current values
    // Must be done in constructor after dependency injection
    const initialWidth = this._window.innerWidth;
    const initialHeight = this._window.innerHeight;

    this.widthBehaviourSubject$ = new BehaviorSubject<number>(initialWidth);
    this.heightBehaviourSubject$ = new BehaviorSubject<number>(initialHeight);
    this.resizeBehaviourSubject$ = new BehaviorSubject<Event>(this._createResizeEvent());

    // Create a shared resize observable to prevent multiple event listeners
    // The service is singleton, so this will only be created once
    this._resizeSubscription = fromEvent<Event>(this._window, 'resize')
      .pipe(debounceTime(100), share())
      .subscribe((event: Event): void => {
        // Update resize event
        this.resizeBehaviourSubject$.next(event);

        // Update width and height
        this.widthBehaviourSubject$.next(this._window.innerWidth);
        this.heightBehaviourSubject$.next(this._window.innerHeight);
      });
  }

  public ngOnDestroy(): void {
    // Clean up subscription when service is destroyed (though unlikely for singleton)
    if (this._resizeSubscription && !this._resizeSubscription.closed) {
      this._resizeSubscription.unsubscribe();
    }
  }

  /**
   * @description
   * Get current window width.
   * @returns {number} number  Current window inner width in pixels.
   */
  public getWidth(): number {
    return this._window.innerWidth;
  }

  /**
   * @description
   * Get current window height.
   * @returns {number} Current window inner height in pixels.
   */
  public getHeight(): number {
    return this._window.innerHeight;
  }

  /**
   * @description
   * Check if window width is less than the specified breakpoint.
   * @param {number} breakpoint - Breakpoint width in pixels.
   * @returns {boolean} True if current width is less than breakpoint.
   */
  public isWidthLessThan(breakpoint: number): boolean {
    return this.getWidth() < breakpoint;
  }

  /**
   * @description
   * Check if window width is greater than or equal to the specified breakpoint.
   * @param {number} breakpoint - Breakpoint width in pixels.
   * @returns {boolean} True if current width is greater than or equal to breakpoint.
   */
  public isWidthGreaterThanOrEqual(breakpoint: number): boolean {
    return this.getWidth() >= breakpoint;
  }

  /**
   * @description
   * Create a synthetic resize event for initial emission.
   * This ensures subscribers get an initial value immediately.
   * @returns {Event} Synthetic resize event.
   */
  private _createResizeEvent(): Event {
    return new Event('resize');
  }
}
